import { useState, type ReactNode } from 'react';
import {
  Box,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderFilters,
  DataTableHeaderRow,
  DataTableHeaderSavedViews,
  DataTablePagination,
  DataTableTable,
  Panel,
  PanelInset,
} from '@customerio/pluma-components/react';
import * as styles from './table-shell.css';

/**
 * What every list in the data index shares: the box someone types a search
 * into, and the frame the rows are drawn in.
 *
 * The lists themselves have little else in common — one is editable and
 * selectable and filtered, another is read-only — so what is shared here is
 * the chrome around them rather than a table component they both configure.
 */

/**
 * The text in the search box, which is not yet the search.
 *
 * Searching costs a request, so it happens on Enter or on clearing the box
 * rather than on every keystroke. The box therefore holds text the URL does
 * not — until the list changes under it. `view` names everything the list is
 * showing, the committed search among it, and any change to it puts the box
 * back to the committed search: a new search, a page, a filter, another
 * scope, or another tab. The page this replaces does the same, resetting its
 * `searchInput` on every load of the list (`type/list/controller.ts:604-609`),
 * so the box never shows text the list is not applying.
 *
 * Reset during render rather than compared on read: a comparison would bring
 * a draft back whenever the list returned to the view it was typed in.
 */
export function useSearchDraft(committed: string, view: string) {
  const [draft, setDraft] = useState({ view, text: committed });

  if (draft.view !== view) {
    setDraft({ view, text: committed });
  }

  return {
    text: draft.view === view ? draft.text : committed,
    setText: (text: string) => setDraft({ view, text }),
  };
}

type DataIndexTableFrameProps = {
  /**
   * Laid over the saved views while it is set — the bulk edit's bar, as the
   * page this replaces lays its editing header over the same row. The
   * search, the filters and the pager stay where they are.
   */
  banner?: ReactNode;
};

/**
 * The rows and the pager, inside the panel the data index draws them in.
 *
 * Rendered as a `DataTable` child, so it reads the table from context rather
 * than taking it as a prop.
 */
export function DataIndexTableFrame({ banner }: DataIndexTableFrameProps) {
  return (
    <>
      <Box className={styles.frame}>
        {banner == null ? null : <Box className={styles.coveringBanner}>{banner}</Box>}
        <Panel>
          <DataIndexTableHeader isSavedViewsCovered={banner != null} />
          <PanelInset>
            <DataTableTable />
          </PanelInset>
        </Panel>
      </Box>
      <DataTablePagination mt="200" />
    </>
  );
}

type DataIndexTableHeaderProps = {
  isSavedViewsCovered: boolean;
};

/**
 * Pluma's own header rows, composed as `DataTableHeader` composes them in
 * Pluma 3.6.0 (`DataTableHeader` with no children) — the
 * pager at the end of the last row there is — so the saved views can be
 * taken out of reach while a banner covers them: they stay in the layout,
 * and nobody tabs onto a control they cannot see.
 */
function DataIndexTableHeader({ isSavedViewsCovered }: DataIndexTableHeaderProps) {
  return (
    <DataTableHeader>
      {({ shouldShowSavedViews, shouldShowActions, shouldShowFilters }) => {
        const lastRow = shouldShowFilters ? 'filters' : shouldShowActions ? 'actions' : 'saved-views';
        return (
          <>
            {shouldShowSavedViews ? (
              <DataTableHeaderRow inert={isSavedViewsCovered}>
                <DataTableHeaderSavedViews />
                {lastRow === 'saved-views' ? <DataTablePagination /> : null}
              </DataTableHeaderRow>
            ) : null}
            {shouldShowActions ? (
              <DataTableHeaderRow>
                <DataTableHeaderActions />
                {lastRow === 'actions' ? <DataTablePagination /> : null}
              </DataTableHeaderRow>
            ) : null}
            {shouldShowFilters ? (
              <DataTableHeaderRow>
                <DataTableHeaderFilters />
                <DataTablePagination />
              </DataTableHeaderRow>
            ) : null}
          </>
        );
      }}
    </DataTableHeader>
  );
}
